export type DemoTemplate = {
  id: string;
  title: string;
  description: string;
  icon: string;
  hidden?: boolean;
  codeTabs: {
    html: string;
    css: string;
    js: string;
  };
};

export const DEMO_TEMPLATES: DemoTemplate[] = [
  {
    id: 'vanilla-landing',
    title: 'Vanilla Landing',
    description: 'Pure HTML, CSS & JavaScript',
    icon: '\uD83C\uDFE0',
    codeTabs: {
      html: `<div class="landing">
  <nav class="nav">
    <div class="nav-brand">MyApp</div>
    <div class="nav-links">
      <a href="#features">Features</a>
      <a href="#pricing">Pricing</a>
      <a href="#" class="btn btn-primary">Get Started</a>
    </div>
  </nav>

  <section class="hero">
    <h1>Build Something Amazing</h1>
    <p>The fastest way to turn your ideas into reality. Start building today.</p>
    <div class="hero-buttons">
      <a href="#" class="btn btn-primary btn-lg">Start Free Trial</a>
      <a href="#" class="btn btn-outline btn-lg">Watch Demo</a>
    </div>
  </section>

  <section id="features" class="features">
    <h2>Why Choose Us</h2>
    <div class="features-grid">
      <div class="feature-card">
        <div class="feature-icon">\u26A1</div>
        <h3>Lightning Fast</h3>
        <p>Optimized for speed and performance out of the box.</p>
      </div>
      <div class="feature-card">
        <div class="feature-icon">\uD83C\uDFA8</div>
        <h3>Beautiful Design</h3>
        <p>Crafted with attention to every pixel and detail.</p>
      </div>
      <div class="feature-card">
        <div class="feature-icon">\uD83D\uDD12</div>
        <h3>Secure</h3>
        <p>Enterprise-grade security for your peace of mind.</p>
      </div>
    </div>
  </section>

  <footer class="footer">
    <p class="footer-highlight">This is a live code editor &mdash; your changes update the URL in real-time.</p>
    <p>Share the URL with anyone to show your code. Sign up for Merlin AI, file uploads, 20+ projects, and more.</p>
  </footer>
</div>`,
      css: `* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #1a1a2e;
  background: #ffffff;
}

.nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  border-bottom: 1px solid #eee;
}

.nav-brand {
  font-size: 1.5rem;
  font-weight: 700;
  color: #7c3aed;
}

.nav-links {
  display: flex;
  gap: 1.5rem;
  align-items: center;
}

.nav-links a {
  text-decoration: none;
  color: #666;
  transition: color 0.2s;
}

.nav-links a:hover {
  color: #7c3aed;
}

.btn {
  display: inline-block;
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s;
  cursor: pointer;
}

.btn-primary {
  background: #7c3aed;
  color: white;
}

.btn-primary:hover {
  background: #6d28d9;
}

.btn-outline {
  border: 2px solid #7c3aed;
  color: #7c3aed;
}

.btn-outline:hover {
  background: #7c3aed;
  color: white;
}

.btn-lg {
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
}

.hero {
  text-align: center;
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
}

.hero h1 {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #1a1a2e;
}

.hero p {
  font-size: 1.25rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto 2rem;
}

.hero-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.features {
  padding: 5rem 2rem;
  text-align: center;
}

.features h2 {
  font-size: 2rem;
  margin-bottom: 3rem;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  max-width: 900px;
  margin: 0 auto;
}

.feature-card {
  padding: 2rem;
  border-radius: 12px;
  border: 1px solid #eee;
  transition: box-shadow 0.2s;
}

.feature-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.feature-icon {
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.feature-card h3 {
  margin-bottom: 0.5rem;
}

.feature-card p {
  color: #666;
}

.footer {
  text-align: center;
  padding: 2rem;
  border-top: 1px solid #eee;
  color: #999;
  font-size: 0.9rem;
  line-height: 1.6;
}

.footer-highlight {
  color: #7c3aed;
  font-weight: 600;
  margin-bottom: 0.25rem;
}`,
      js: `// Add smooth scrolling for anchor links
document.querySelectorAll('a[href^="#"]').forEach(anchor => {
  anchor.addEventListener('click', function (e) {
    e.preventDefault();
    const target = document.querySelector(this.getAttribute('href'));
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  });
});

// Add scroll animation for feature cards
const observer = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      entry.target.style.opacity = '1';
      entry.target.style.transform = 'translateY(0)';
    }
  });
}, { threshold: 0.1 });

document.querySelectorAll('.feature-card').forEach(card => {
  card.style.opacity = '0';
  card.style.transform = 'translateY(20px)';
  card.style.transition = 'opacity 0.5s, transform 0.5s';
  observer.observe(card);
});`,
    },
  },
  {
    id: 'react-counter',
    title: 'React Counter',
    description: 'React 18 with hooks & JSX',
    icon: '\u269B\uFE0F',
    codeTabs: {
      html: `<div id="root"></div>`,
      css: `* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: #1a1a2e;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
}

.counter {
  text-align: center;
  background: #16213e;
  padding: 3rem 4rem;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.counter h1 {
  font-size: 1.5rem;
  margin-bottom: 2rem;
  color: #a78bfa;
}

.count {
  font-size: 5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  color: white;
}

.buttons {
  display: flex;
  gap: 1rem;
}

.btn {
  padding: 0.75rem 2rem;
  font-size: 1.25rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-decrement {
  background: #ef4444;
  color: white;
}

.btn-decrement:hover {
  background: #dc2626;
}

.btn-reset {
  background: #6b7280;
  color: white;
}

.btn-reset:hover {
  background: #4b5563;
}

.btn-increment {
  background: #7c3aed;
  color: white;
}

.btn-increment:hover {
  background: #6d28d9;
}`,
      js: `const { useState } = React;

function Counter() {
  const [count, setCount] = useState(0);

  return (
    <div className="counter">
      <h1>React Counter</h1>
      <div className="count">{count}</div>
      <div className="buttons">
        <button className="btn btn-decrement" onClick={() => setCount(c => c - 1)}>
          -
        </button>
        <button className="btn btn-reset" onClick={() => setCount(0)}>
          Reset
        </button>
        <button className="btn btn-increment" onClick={() => setCount(c => c + 1)}>
          +
        </button>
      </div>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<Counter />);`,
    },
  },
  {
    id: 'vue-todo',
    title: 'Vue Todo App',
    description: 'Vue 3 Composition API',
    icon: '\uD83D\uDFE2',
    codeTabs: {
      html: `<div id="app">
  <div class="todo-container">
    <h1>Vue Todo List</h1>
    <form @submit.prevent="addTodo">
      <div class="todo-form">
        <input
          v-model="newTodo"
          type="text"
          placeholder="What needs to be done?"
          autocomplete="off"
        />
        <button type="submit" :disabled="!newTodo.trim()">Add</button>
      </div>
    </form>
    <ul class="todo-list">
      <li
        v-for="(todo, index) in todos"
        :key="index"
        class="todo-item"
        :class="{ completed: todo.completed }"
      >
        <input
          type="checkbox"
          class="todo-checkbox"
          :checked="todo.completed"
          @change="toggleTodo(index)"
        />
        <span class="todo-text">{{ todo.text }}</span>
        <button class="todo-delete" @click="deleteTodo(index)">\u00D7</button>
      </li>
    </ul>
    <div class="todo-stats" v-if="todos.length">
      {{ remaining }} item{{ remaining !== 1 ? 's' : '' }} remaining
    </div>
    <div class="todo-stats" v-else>
      No todos yet. Add one above!
    </div>
  </div>
</div>`,
      css: `* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: #f5f3ff;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
}

.todo-container {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  height: fit-content;
}

h1 {
  color: #42b883;
  margin-bottom: 1.5rem;
  font-size: 1.75rem;
}

.todo-form {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.todo-form input {
  flex: 1;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  outline: none;
  transition: border-color 0.2s;
}

.todo-form input:focus {
  border-color: #42b883;
}

.todo-form button {
  padding: 0.75rem 1.5rem;
  background: #42b883;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.todo-form button:hover {
  background: #38a376;
}

.todo-form button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.todo-list {
  list-style: none;
}

.todo-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f3f4f6;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from { opacity: 0; transform: translateX(-10px); }
  to { opacity: 1; transform: translateX(0); }
}

.todo-item.completed .todo-text {
  text-decoration: line-through;
  color: #9ca3af;
}

.todo-checkbox {
  width: 20px;
  height: 20px;
  accent-color: #42b883;
  cursor: pointer;
}

.todo-text {
  flex: 1;
  font-size: 1rem;
}

.todo-delete {
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  font-size: 1.25rem;
  opacity: 0;
  transition: opacity 0.2s;
}

.todo-item:hover .todo-delete {
  opacity: 1;
}

.todo-stats {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
  color: #9ca3af;
  font-size: 0.875rem;
}`,
      js: `const { createApp, ref, computed } = Vue;

createApp({
  setup() {
    const newTodo = ref('');
    const todos = ref([]);

    const remaining = computed(() =>
      todos.value.filter(t => !t.completed).length
    );

    function addTodo() {
      const text = newTodo.value.trim();
      if (text) {
        todos.value.push({ text, completed: false });
        newTodo.value = '';
      }
    }

    function toggleTodo(index) {
      todos.value[index].completed = !todos.value[index].completed;
    }

    function deleteTodo(index) {
      todos.value.splice(index, 1);
    }

    return { newTodo, todos, remaining, addTodo, toggleTodo, deleteTodo };
  }
}).mount('#app');`,
    },
  },
  {
    id: 'svelte-counter',
    title: 'Svelte Counter',
    description: 'Compiled reactivity via CDN',
    hidden: true,
    icon: '\uD83D\uDD36',
    codeTabs: {
      html: `<div id="app"></div>`,
      css: `* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: #1a1a2e;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
}`,
      js: `import { importSvelteBundle } from 'https://unpkg.com/svelte-browser-import@0.0.5/dist/svelte-browser-import.es.js';

const svelteCode = \`
<script>
  let count = 0;
</script>

<div class="counter">
  <h1>Svelte Counter</h1>
  <div class="count">{count}</div>
  <div class="buttons">
    <button on:click={() => count--}>-</button>
    <button on:click={() => count = 0}>Reset</button>
    <button on:click={() => count++}>+</button>
  </div>
</div>

<style>
  .counter {
    text-align: center;
    background: #16213e;
    padding: 3rem 4rem;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }
  h1 {
    font-size: 1.5rem;
    margin-bottom: 2rem;
    color: #ff3e00;
  }
  .count {
    font-size: 5rem;
    font-weight: 700;
    margin-bottom: 2rem;
  }
  .buttons {
    display: flex;
    gap: 1rem;
    justify-content: center;
  }
  button {
    padding: 0.75rem 2rem;
    font-size: 1.25rem;
    font-weight: 600;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    background: #ff3e00;
    color: white;
  }
  button:hover {
    background: #e63600;
    transform: translateY(-1px);
  }
  button:nth-child(2) {
    background: #6b7280;
  }
  button:nth-child(2):hover {
    background: #4b5563;
  }
</style>
\`;

async function init() {
  try {
    const bundle = await importSvelteBundle({
      files: [{ name: 'App', type: 'svelte', content: svelteCode, modified: true }],
      packagesUrl: 'https://unpkg.com',
      svelteUrl: 'https://unpkg.com/svelte@4'
    });
    new bundle.render({ target: document.getElementById('app') });
  } catch (e) {
    document.getElementById('app').innerHTML =
      '<div style="color:#ff3e00;padding:2rem;text-align:center;">' +
      '<h2>Svelte Loading...</h2><p>' + e.message + '</p></div>';
  }
}

init();`,
    },
  },
  {
    id: 'angular-counter',
    title: 'Angular Counter',
    description: 'Angular JIT via ESM bundles',
    hidden: true,
    icon: '\uD83D\uDFE5',
    codeTabs: {
      html: `<app-root></app-root>`,
      css: `* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: #1a1a2e;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
}

app-root {
  display: block;
  text-align: center;
  background: #16213e;
  padding: 3rem 4rem;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

app-root h1 {
  font-size: 1.5rem;
  margin-bottom: 2rem;
  color: #dd0031;
}

.count {
  font-size: 5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  color: white;
}

.buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.buttons button {
  padding: 0.75rem 2rem;
  font-size: 1.25rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: white;
}

.btn-dec { background: #ef4444; }
.btn-dec:hover { background: #dc2626; }
.btn-reset { background: #6b7280; }
.btn-reset:hover { background: #4b5563; }
.btn-inc { background: #dd0031; }
.btn-inc:hover { background: #c3002f; }

.info {
  margin-top: 2rem;
  padding: 1rem;
  background: #1e293b;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #94a3b8;
}`,
      js: `import { Component } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';

const AppComponent = Component({
  selector: 'app-root',
  standalone: true,
  template: \`
    <h1>Angular Counter</h1>
    <div class="count">{{ count }}</div>
    <div class="buttons">
      <button class="btn-dec" (click)="decrement()">-</button>
      <button class="btn-reset" (click)="reset()">Reset</button>
      <button class="btn-inc" (click)="increment()">+</button>
    </div>
    <div class="info">
      Total clicks: {{ totalClicks }}
    </div>
  \`
})(class {
  count = 0;
  totalClicks = 0;

  increment() {
    this.count++;
    this.totalClicks++;
  }

  decrement() {
    this.count--;
    this.totalClicks++;
  }

  reset() {
    this.count = 0;
  }
});

bootstrapApplication(AppComponent).catch(err => console.error(err));`,
    },
  },
  {
    id: 'typescript-quiz',
    title: 'TypeScript Quiz',
    description: 'Type-safe quiz with Babel TS',
    icon: '\uD83D\uDCD8',
    codeTabs: {
      html: `<div class="quiz-container">
  <div class="quiz-header">
    <div class="progress-bar">
      <div class="progress-fill" id="progress"></div>
    </div>
    <div class="quiz-info">
      <span id="question-count">Question 1/5</span>
      <span id="score">Score: 0</span>
    </div>
  </div>
  <div id="quiz-body">
    <h2 id="question" class="question"></h2>
    <div id="options" class="options"></div>
  </div>
  <div id="quiz-result" class="quiz-result" style="display: none">
    <h2>Quiz Complete!</h2>
    <div class="final-score" id="final-score"></div>
    <p id="result-message"></p>
    <button class="btn" id="restart-btn">Play Again</button>
  </div>
</div>`,
      css: `* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: #0f172a;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 1rem;
}

.quiz-container {
  background: #1e293b;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 540px;
}

.progress-bar {
  height: 6px;
  background: #334155;
  border-radius: 3px;
  margin-bottom: 1rem;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3178c6, #5a9bd5);
  border-radius: 3px;
  transition: width 0.4s ease;
  width: 20%;
}

.quiz-info {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #94a3b8;
  margin-bottom: 2rem;
}

.question {
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.option-btn {
  padding: 1rem 1.25rem;
  background: #334155;
  border: 2px solid transparent;
  border-radius: 10px;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.option-btn:hover {
  border-color: #3178c6;
  background: #3b4c6b;
}

.option-btn.correct {
  border-color: #22c55e;
  background: rgba(34, 197, 94, 0.15);
}

.option-btn.wrong {
  border-color: #ef4444;
  background: rgba(239, 68, 68, 0.15);
}

.option-btn:disabled {
  cursor: default;
}

.quiz-result {
  text-align: center;
}

.quiz-result h2 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
}

.final-score {
  font-size: 3rem;
  font-weight: 700;
  color: #3178c6;
  margin-bottom: 0.5rem;
}

#result-message {
  color: #94a3b8;
  margin-bottom: 2rem;
}

.btn {
  padding: 0.75rem 2rem;
  background: #3178c6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn:hover {
  background: #2563a0;
}`,
      js: `interface Question {
  question: string;
  options: string[];
  correct: number;
}

interface QuizState {
  currentQuestion: number;
  score: number;
  questions: Question[];
}

const questions: Question[] = [
  {
    question: "What does CSS stand for?",
    options: ["Computer Style Sheets", "Cascading Style Sheets", "Creative Style System", "Colorful Style Sheets"],
    correct: 1
  },
  {
    question: "Which HTML tag is used for the largest heading?",
    options: ["<heading>", "<h6>", "<h1>", "<head>"],
    correct: 2
  },
  {
    question: "What does 'DOM' stand for?",
    options: ["Document Object Model", "Data Object Management", "Digital Ordinance Model", "Document Order Mode"],
    correct: 0
  },
  {
    question: "Which property changes text color in CSS?",
    options: ["font-color", "text-color", "color", "foreground-color"],
    correct: 2
  },
  {
    question: "What symbol is used for single-line comments in JS?",
    options: ["/* */", "#", "//", "<!--  -->"],
    correct: 2
  }
];

const state: QuizState = {
  currentQuestion: 0,
  score: 0,
  questions
};

function getElement(id: string) {
  return document.getElementById(id);
}

function loadQuestion(): void {
  const q: Question = state.questions[state.currentQuestion];
  getElement('question').textContent = q.question;
  getElement('question-count').textContent =
    \`Question \${state.currentQuestion + 1}/\${state.questions.length}\`;
  getElement('progress').style.width =
    \`\${((state.currentQuestion + 1) / state.questions.length) * 100}%\`;

  const optionsEl = getElement('options');
  optionsEl.innerHTML = '';

  q.options.forEach((opt: string, i: number) => {
    const btn = document.createElement('button');
    btn.className = 'option-btn';
    btn.textContent = opt;
    btn.addEventListener('click', () => selectAnswer(i, btn));
    optionsEl.appendChild(btn);
  });
}

function selectAnswer(index: number, btn: HTMLButtonElement): void {
  const buttons = document.querySelectorAll('.option-btn');
  buttons.forEach((b) => b.disabled = true);

  const correct: number = state.questions[state.currentQuestion].correct;

  if (index === correct) {
    btn.classList.add('correct');
    state.score++;
    getElement('score').textContent = \`Score: \${state.score}\`;
  } else {
    btn.classList.add('wrong');
    buttons[correct].classList.add('correct');
  }

  setTimeout((): void => {
    state.currentQuestion++;
    if (state.currentQuestion < state.questions.length) {
      loadQuestion();
    } else {
      showResult();
    }
  }, 1200);
}

function showResult(): void {
  getElement('quiz-body').style.display = 'none';
  const result = getElement('quiz-result');
  result.style.display = 'block';
  getElement('final-score').textContent =
    \`\${state.score}/\${state.questions.length}\`;

  const pct: number = (state.score / state.questions.length) * 100;
  const msg: string = pct === 100 ? "Perfect score!" :
    pct >= 60 ? "Great job!" : "Keep practicing!";
  getElement('result-message').textContent = msg;
}

function restartQuiz(): void {
  state.currentQuestion = 0;
  state.score = 0;
  getElement('score').textContent = 'Score: 0';
  getElement('quiz-body').style.display = 'block';
  getElement('quiz-result').style.display = 'none';
  loadQuestion();
}

getElement('restart-btn').addEventListener('click', restartQuiz);
loadQuestion();`,
    },
  },
  {
    id: 'css-animations',
    title: 'CSS Animations',
    description: 'Interactive animation showcase',
    icon: '\uD83C\uDFA8',
    codeTabs: {
      html: `<div class="playground">
  <h1>CSS Animations</h1>
  <p class="subtitle">Click any card to replay its animation</p>

  <div class="grid">
    <div class="card" onclick="replay(this)">
      <div class="demo">
        <div class="ball"></div>
      </div>
      <span class="label">Bounce</span>
    </div>

    <div class="card" onclick="replay(this)">
      <div class="demo">
        <div class="square"></div>
      </div>
      <span class="label">Spin</span>
    </div>

    <div class="card" onclick="replay(this)">
      <div class="demo">
        <div class="circle"></div>
      </div>
      <span class="label">Pulse</span>
    </div>

    <div class="card" onclick="replay(this)">
      <div class="demo">
        <div class="box"></div>
      </div>
      <span class="label">Shake</span>
    </div>

    <div class="card" onclick="replay(this)">
      <div class="demo">
        <div class="diamond"></div>
      </div>
      <span class="label">Fade</span>
    </div>

    <div class="card" onclick="replay(this)">
      <div class="demo">
        <div class="bar"></div>
      </div>
      <span class="label">Slide</span>
    </div>
  </div>
</div>`,
      css: `* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: #0f172a;
  color: white;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
}

.playground {
  max-width: 90%;
  text-align: center;
}

h1 {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #7c3aed, #ec4899);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.subtitle {
  color: #64748b;
  margin-bottom: 2rem;
}

.grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
  width: 100%;
}

.card {
  background: #1e293b;
  border-radius: 12px;
  padding: 1.25rem 1rem;
  cursor: pointer;
  transition: transform 0.2s, border-color 0.2s;
  border: 1px solid #334155;
}

.card:hover {
  transform: translateY(-2px);
  border-color: #7c3aed;
}

.demo {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
}

.label {
  font-size: 0.85rem;
  color: #94a3b8;
  font-weight: 500;
}

.ball {
  width: 24px;
  height: 24px;
  background: #7c3aed;
  border-radius: 50%;
  animation: bounce 1s ease infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

.square {
  width: 24px;
  height: 24px;
  background: #ec4899;
  border-radius: 4px;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.circle {
  width: 24px;
  height: 24px;
  background: #22c55e;
  border-radius: 50%;
  animation: pulse-anim 1.5s ease-in-out infinite;
}

@keyframes pulse-anim {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.4); opacity: 0.6; }
}

.box {
  width: 24px;
  height: 24px;
  background: #f59e0b;
  border-radius: 4px;
  animation: shake 0.6s ease-in-out infinite;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-8px); }
  75% { transform: translateX(8px); }
}

.diamond {
  width: 24px;
  height: 24px;
  background: #06b6d4;
  transform: rotate(45deg);
  animation: fade 2s ease-in-out infinite;
}

@keyframes fade {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.2; }
}

.bar {
  width: 32px;
  height: 8px;
  background: #ef4444;
  border-radius: 4px;
  animation: slide 1.5s ease-in-out infinite;
}

@keyframes slide {
  0%, 100% { transform: translateX(-20px); }
  50% { transform: translateX(20px); }
}`,
      js: `function replay(card) {
  const demo = card.querySelector('.demo > *');
  demo.style.animation = 'none';
  demo.offsetHeight; // trigger reflow
  demo.style.animation = '';
}`,
    },
  },
  {
    id: 'blank',
    title: 'Blank Canvas',
    description: 'Start from scratch',
    icon: '\uD83D\uDCC4',
    codeTabs: {
      html: `<div id="app">
  <h1>Hello, World!</h1>
  <p>Start building something amazing.</p>
</div>`,
      css: `* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #1a1a2e;
  color: white;
}

#app {
  text-align: center;
}

h1 {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #7c3aed, #a78bfa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

p {
  color: #9ca3af;
  font-size: 1.1rem;
}`,
      js: `// Your JavaScript code here
console.log('Ready to build!');`,
    },
  },
];
